import type { GameData } from '../game/types';

export class TooltipManager {
  private tooltipElement: HTMLElement | null = null;
  private showTimeout: number | null = null;
  private hideTimeout: number | null = null;
  private currentTarget: HTMLElement | null = null;
  private currentTooltipKey: string | null = null;
  private tooltips: Record<string, any>;
  private lastMouseEvent: MouseEvent | null = null;
  private boundMouseOver: (e: MouseEvent) => void;
  private boundMouseOut: (e: MouseEvent) => void;
  private boundMouseMove: (e: MouseEvent) => void;

  constructor(
    private container: HTMLElement,
    gameData: GameData
  ) {
    this.tooltips = gameData.tooltips || {};
    // Bind event handlers once and store references
    this.boundMouseOver = this.handleMouseOver.bind(this);
    this.boundMouseOut = this.handleMouseOut.bind(this);
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.setup();
  }

  private setup(): void {
    // Use event delegation on document to catch all events, including dynamically created content
    document.addEventListener('mouseover', this.boundMouseOver, true);
    document.addEventListener('mouseout', this.boundMouseOut, true);
    document.addEventListener('mousemove', this.boundMouseMove, true);
  }

  private handleMouseOver(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target) return;
    
    // Find the element with tooltip key (could be target or parent)
    const tooltipKey = this.getTooltipKey(target);
    const elementWithTooltip = this.getElementWithTooltip(target);
    
    if (tooltipKey && elementWithTooltip) {
      // Check if we're already showing/scheduling a tooltip for this key
      if (this.currentTooltipKey === tooltipKey) {
        // Same tooltip key - just update position if visible, don't reschedule
        this.lastMouseEvent = e;
        if (this.tooltipElement && this.tooltipElement.style.display === 'block') {
          this.updatePosition(e);
        }
        // Don't reschedule - let existing timeout complete
        return;
      }
      
      // Different tooltip key - cancel any pending show and hide current tooltip
      this.cancelShow();
      this.hide();
      
      // Set new target and key, then schedule show
      this.currentTarget = elementWithTooltip;
      this.currentTooltipKey = tooltipKey;
      this.scheduleShow(tooltipKey, e);
    } else {
      // No tooltip key found - check if we're still within current target
      if (this.currentTarget && this.currentTarget.contains(target)) {
        // Still within current tooltip element
        this.lastMouseEvent = e;
        if (this.tooltipElement && this.tooltipElement.style.display === 'block') {
          this.updatePosition(e);
        }
        return;
      }
      
      // Actually leaving tooltip area
      if (this.currentTarget) {
        this.cancelShow();
        this.hide();
        this.currentTarget = null;
        this.currentTooltipKey = null;
      }
    }
  }
  
  private getElementWithTooltip(element: HTMLElement): HTMLElement | null {
    if (!element) return null;
    
    // Check if this element has the tooltip key
    if (element.dataset && element.dataset.tooltipKey) {
      return element;
    }
    
    // Check getAttribute as fallback
    if (element.getAttribute('data-tooltip-key')) {
      return element;
    }
    
    // Check parent elements
    let current: HTMLElement | null = element.parentElement;
    while (current && current !== document.body) {
      if (current.dataset && current.dataset.tooltipKey) {
        return current;
      }
      if (current.getAttribute('data-tooltip-key')) {
        return current;
      }
      current = current.parentElement;
    }
    
    return null;
  }

  private handleMouseOut(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    
    if (!this.currentTarget) return;
    
    // Check if we're leaving the element with tooltip
    const isLeavingTooltipElement = target === this.currentTarget || this.currentTarget.contains(target);
    
    if (isLeavingTooltipElement) {
      // Check if we're moving to a child element (shouldn't hide)
      if (relatedTarget && this.currentTarget.contains(relatedTarget)) {
        return; // Still within the tooltip element
      }
      
      // Check if we're moving to another element with a tooltip (let mouseover handle it)
      if (relatedTarget) {
        const newTooltipKey = this.getTooltipKey(relatedTarget);
        if (newTooltipKey) {
          // Moving to another tooltip element - let mouseover handle the transition
          return;
        }
      }
      
      // Actually leaving - hide the tooltip
      this.cancelShow();
      this.hide();
      this.currentTarget = null;
      this.currentTooltipKey = null;
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    // Store the latest mouse event for position updates
    this.lastMouseEvent = e;
    
    if (this.tooltipElement && this.currentTarget && this.tooltipElement.style.display === 'block') {
      this.updatePosition(e);
    }
  }

  private getTooltipKey(element: HTMLElement): string | null {
    if (!element) return null;
    
    // Check for data-tooltip-key attribute (dataset uses camelCase: tooltipKey)
    if (element.dataset && element.dataset.tooltipKey) {
      return element.dataset.tooltipKey;
    }
    
    // Also check getAttribute as fallback (in case dataset doesn't work)
    const attrValue = element.getAttribute('data-tooltip-key');
    if (attrValue) {
      return attrValue;
    }
    
    // Check parent elements (for nested elements)
    let current: HTMLElement | null = element.parentElement;
    while (current && current !== document.body) {
      if (current.dataset && current.dataset.tooltipKey) {
        return current.dataset.tooltipKey;
      }
      const parentAttr = current.getAttribute('data-tooltip-key');
      if (parentAttr) {
        return parentAttr;
      }
      current = current.parentElement;
    }
    
    return null;
  }

  private scheduleShow(tooltipKey: string, e: MouseEvent): void {
    // Cancel any existing timeout
    this.cancelShow();
    
    // Store the event for position calculation
    this.lastMouseEvent = e;
    
    // Store the key we're scheduling for
    const scheduledKey = tooltipKey;
    
    this.showTimeout = window.setTimeout(() => {
      // Check if we're still on the same tooltip key
      if (this.currentTooltipKey === scheduledKey && this.lastMouseEvent) {
        this.show(scheduledKey, this.lastMouseEvent);
      }
      this.showTimeout = null;
    }, 300); // 300ms delay before showing
  }

  private cancelShow(): void {
    if (this.showTimeout !== null) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  private show(tooltipKey: string, e: MouseEvent): void {
    // Double-check we still have a valid target
    if (!this.currentTarget || this.currentTooltipKey !== tooltipKey) {
      return;
    }
    
    const tooltipText = this.getTooltipText(tooltipKey);
    
    if (!tooltipText) {
      return;
    }

    // Create tooltip element if it doesn't exist
    if (!this.tooltipElement) {
      this.tooltipElement = document.createElement('div');
      this.tooltipElement.className = 'custom-tooltip';
      document.body.appendChild(this.tooltipElement);
    }

    this.tooltipElement.textContent = tooltipText;
    
    // Set initial position based on current mouse position
    const padding = 10;
    const x = e.clientX + padding;
    const y = e.clientY + padding;
    
    this.tooltipElement.style.left = `${x}px`;
    this.tooltipElement.style.top = `${y}px`;
    this.tooltipElement.style.zIndex = '99999';
    
    // Show the element
    this.tooltipElement.style.display = 'block';
    this.tooltipElement.style.visibility = 'visible';
    this.tooltipElement.style.opacity = '1';
    
    // Force a reflow
    void this.tooltipElement.offsetHeight;
    
    // Use requestAnimationFrame to ensure the element is laid out before adjusting
    requestAnimationFrame(() => {
      if (this.tooltipElement && this.currentTarget) {
        this.adjustPosition();
      }
    });
  }

  private updatePosition(e: MouseEvent): void {
    if (!this.tooltipElement || this.tooltipElement.style.display !== 'block') {
      return;
    }

    const padding = 10;
    let x = e.clientX + padding;
    let y = e.clientY + padding;
    
    this.tooltipElement.style.left = `${x}px`;
    this.tooltipElement.style.top = `${y}px`;
    
    // Adjust after positioning to keep on screen
    this.adjustPosition();
  }

  private adjustPosition(): void {
    if (!this.tooltipElement || this.tooltipElement.style.display !== 'block') {
      return;
    }

    const padding = 10;
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    
    // If element isn't laid out yet, skip adjustment
    if (tooltipRect.width === 0 && tooltipRect.height === 0) {
      return;
    }
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = parseFloat(this.tooltipElement.style.left) || 0;
    let y = parseFloat(this.tooltipElement.style.top) || 0;
    
    // Ensure tooltip stays within viewport
    // Check right edge
    if (x + tooltipRect.width > viewportWidth) {
      x = Math.max(padding, viewportWidth - tooltipRect.width - padding);
    }
    // Check left edge
    if (x < padding) {
      x = padding;
    }
    // Check bottom edge
    if (y + tooltipRect.height > viewportHeight) {
      y = Math.max(padding, viewportHeight - tooltipRect.height - padding);
    }
    // Check top edge
    if (y < padding) {
      y = padding;
    }
    
    this.tooltipElement.style.left = `${x}px`;
    this.tooltipElement.style.top = `${y}px`;
  }

  private hide(): void {
    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    
    if (this.tooltipElement) {
      // Hide immediately for better responsiveness
      this.tooltipElement.style.display = 'none';
      this.tooltipElement.style.opacity = '0';
      this.tooltipElement.style.visibility = 'hidden';
    }
  }

  private getTooltipText(key: string): string | null {
    // Parse key format: "category.item" or "category.subcategory.item"
    const parts = key.split('.');
    
    if (parts.length < 2) {
      return null;
    }
    
    // Map short category names to full YAML structure names
    const categoryMap: Record<string, string> = {
      'ability': 'special_abilities',
      'action': 'action_buttons',
      'property': 'properties',
      'resource': 'resources',
      'research_type': 'research_types',
      'silver_bullet': 'silver_bullets',
      'reality_effect': 'reality_effects'
    };
    
    // Map the first part if needed
    const mappedCategory = categoryMap[parts[0]] || parts[0];
    const mappedParts = [mappedCategory, ...parts.slice(1)];
    
    let current: unknown = this.tooltips;
    
    for (const part of mappedParts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return null;
      }
    }
    
    return typeof current === 'string' ? current : null;
  }

  destroy(): void {
    this.cancelShow();
    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout);
    }
    this.hide();
    this.currentTarget = null;
    this.currentTooltipKey = null;
    this.lastMouseEvent = null;
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
    // Remove event listeners using stored bound references
    document.removeEventListener('mouseover', this.boundMouseOver, true);
    document.removeEventListener('mouseout', this.boundMouseOut, true);
    document.removeEventListener('mousemove', this.boundMouseMove, true);
  }
}
